"use client";

import { Tooltip, TooltipContent } from "@/components/ui/tooltip";
import { getCurrentTimeZoneName } from "@/lib/timezone";
import { cn } from "@/lib/utils";
import { TooltipProvider, TooltipTrigger } from "@radix-ui/react-tooltip";
import { useCallback, useEffect, useState } from "react";

const EventTimer = ({
	dateTime,
	classNames,
}: {
	dateTime: Date;
	classNames?: {container?: string, tile?: string; timeCount?: string; timeUnit?: string };
}) => {
	const [timeLeft, setTimeLeft] = useState({
		days: 0,
		hours: 0,
		minutes: 0,
		seconds: 0,
	});

	const calculateTimeLeft = useCallback(() => {
		const { timeZone } = Intl.DateTimeFormat().resolvedOptions();

		const eventDate = new Date(dateTime.toLocaleString("en-US", { timeZone }));
		const now = new Date(new Date().toLocaleString("en-US", { timeZone: timeZone }));

		const difference = eventDate.getTime() - now.getTime();

		if (difference > 0) {
			setTimeLeft(() => ({
				days: Math.floor(difference / (1000 * 60 * 60 * 24)),
				hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
				minutes: Math.floor((difference / 1000 / 60) % 60),
				seconds: Math.floor((difference / 1000) % 60),
			}));
		} else {
			setTimeLeft({
				days: 0,
				hours: 0,
				minutes: 0,
				seconds: 0,
			});
		}
	}, [dateTime]);

	useEffect(() => {
		calculateTimeLeft();

		const timer = setInterval(() => {
			calculateTimeLeft();
		}, 1000);

		return () => clearInterval(timer);
	}, [calculateTimeLeft, dateTime]);

	useEffect(() => {
		// Handle time zone change by recalculating time left
		calculateTimeLeft();
	}, [calculateTimeLeft, dateTime]);

	return (
		<div className="text-center">
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger>
						<div className={cn(
							"-mt-12 flex items-center justify-center gap-4 lg:gap-6",
							classNames?.container
						)}>
							{Object.entries(timeLeft).map(([timerUnit, timerValue]) => (
								<div
									key={timerUnit}
									className={cn(
										"flex h-20 w-20 flex-col items-center justify-center gap-1 overflow-hidden rounded-lg bg-black text-white lg:h-24 lg:w-24 lg:p-6",
										classNames?.tile
									)}
								>
									<span className={cn("text-3xl font-black lg:text-4xl", classNames?.timeCount)}>
										{timerValue}
									</span>
									<span className={cn("text-xxs font-bold uppercase tracking-wide", classNames?.timeUnit)}>
										{timerUnit}
									</span>
								</div>
							))}
						</div>
					</TooltipTrigger>
					<TooltipContent className="-mt-24 border-0 bg-black px-4 py-2 text-xs font-bold uppercase tracking-wide text-gray-200">
						{getCurrentTimeZoneName()}
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		</div>
	);
};

export default EventTimer;
